import { Injectable } from '@angular/core';
import { HttpErrorStatusCodes } from '../../assets/httpErrorCodes/httpErrorStatusCodes';
import {ToastrService} from 'ngx-toastr';

/**
 * @service CommonMethodsService
 * @description
 * The `CommonMethodsService` is a core utility service designed to standardize and centralize
 * common operations across the AquaCloud application, with a primary focus on error handling
 * and user notifications. It acts as a bridge between technical error responses and
 * user-friendly UI feedback.
 *
 * ## Purpose and Overview
 * In a complex Angular application, handling errors consistently is crucial for a good user experience.
 * This service provides a single entry point for displaying error messages, ensuring that whether
 * an error comes from an HTTP request, a business logic failure, or a validation issue, it is
 * presented to the user in a uniform way using toast notifications.
 *
 * ## Key Responsibilities:
 * 1. **Centralized Error Processing**: It accepts error objects of various types (HTTP responses, strings, objects)
 *    and determines the best way to present them to the user.
 * 2. **HTTP Status Code Mapping**: It maintains logic to map standard and custom HTTP status codes
 *    (e.g., 404, 500, 401) to readable, localized, or specific messages defined in the application's assets.
 * 3. **User Notification**: It integrates with `ToastrService` to display non-blocking error alerts
 *    that inform the user without disrupting their workflow significantly.
 *
 * ## Features:
 * - **Smart Error Detection**: Automatically detects if an input is an HTTP error with a status code.
 * - **Lookup Mechanism**: Iterates through a predefined list of error codes (`HttpErrorStatusCodes`) to find matches.
 * - **Fallback Handling**: Provides a fallback mechanism to display raw error messages if no status code mapping is found.
 * - **Singleton Instance**: Provided in the root injector to ensure a single instance manages alerts application-wide.
 *
 * ## Data Flow:
 * 1. A component or service catches an error (e.g., in a `catch` block of a Promise or Observable).
 * 2. The component calls `commonMethodsService.userFriendlyAlertMessages(error)`.
 * 3. The service inspects the `error` object.
 * 4. If `error.status` exists, it searches `HttpErrorStatusCodes` for a matching `statusCode`.
 * 5. If a match is found, the corresponding `message` is displayed via `toastr.error()`.
 * 6. If no status code exists or no match is found, the raw `error` is displayed via `toastr.error()`.
 *
 * @service-provider root (singleton)
 * @dependencies ToastrService, HttpErrorStatusCodes
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})
export class CommonMethodsService {

  /**
   * @method constructor
   * @description
   * Initializes the `CommonMethodsService` instance.
   *
   * Dependency Injection:
   * - Injects `ToastrService` to enable the display of toast notifications.
   *
   * @param {ToastrService} toastr - @service The library service used for showing growl-style alerts.
   *                                 It is assigned to a public property for potential access,
   *                                 though primarily used internally within this service.
   */
  constructor(public toastr: ToastrService) {}

  /**
   * @method userFriendlyAlertMessages
   * @public
   * @description
   * Processes an error object or message and displays a user-friendly alert notification.
   *
   * This method serves as the global error handler for UI feedback. It is designed to handle
   * both Angular `HttpErrorResponse` objects and simple string error messages.
   *
   * ## Logic Flow:
   * 1. **Check for Status Code**: The method first checks if the provided `error` object has a `status` property.
   *    This typically indicates an HTTP error response.
   * 2. **Status Code Mapping**:
   *    - If a `status` exists, the method iterates through the `HttpErrorStatusCodes` array.
   *    - It compares `error.status` with each `statusCode` in the array.
   *    - If a match is found, it retrieves the user-friendly `message` associated with that code.
   *    - It then calls `this.toastr.error()` with the mapped message.
   * 3. **Fallback/Direct Message**:
   *    - If the `error` object does not have a `status` property (e.g., it's a string or a standard Error object),
   *      the method treats the input `error` as the message itself.
   *    - It calls `this.toastr.error(error)` directly.
   *
   * @param {any} error - The error to be processed. This can be:
   *                      - An `HttpErrorResponse` object containing a `status` code.
   *                      - A string containing the error message.
   *                      - An object with a `status` property.
   *
   * @returns {void}
   *
   * @side-effect
   * - Triggers a UI update by showing a toast notification (red error alert).
   * - Does not return any value or transform data, purely for side effects (UI notification).
   *
   * @example
   * // Usage in a component's try-catch block
   * try {
   *   await this.someService.getData();
   * } catch (error) {
   *   this.commonMethodsService.userFriendlyAlertMessages(error);
   * }
   *
   * @example
   * // Usage with a custom error message
   * if (!isValid) {
   *   this.commonMethodsService.userFriendlyAlertMessages("Invalid input provided.");
   * }
   */
  public userFriendlyAlertMessages(error: any) {
    if (error.status) {
        for (let i = 0; i < HttpErrorStatusCodes.length;i ++) {
          if (error.status == HttpErrorStatusCodes[i].statusCode) {
            this.toastr.error(HttpErrorStatusCodes[i].message);
          }
        }
    } else {
      this.toastr.error(error);
    }
  }
}